import { Button, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { UpdateCandidateBlock } from "../../components/CandidateBlock/UpdateCandidateBlock/UpdateCandidateBlock";
import { Layout } from "../../components/Layout/Layout";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { useFormik } from "formik";
import { IUpdateCandidateBody } from "../../api/candidate/types";
import { useEffect, useState, useCallback } from "react";
import { fetchSingleCandidate, fetchUpdateCandidate } from "../../store/slices/candidates";
import PageLoaderWrapper from "../../components/PageLoaderWrapper";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { UserRole } from "@/api/auth/types";
import { selectUserRole } from "@/store/slices/user";

export const UpdateCandidatePage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { currentCandidate, isLoading } = useAppSelector((state: RootState) => state.candidates);

  const role = useAppSelector(selectUserRole);
  const isAdmin = role === UserRole.ADMIN;

  // Состояние для хранения изначальных значений формы
  const [initialValues, setInitialValues] = useState<IUpdateCandidateBody | null>(null);

  // Функция для сравнения текущих значений с изначальными
  const hasFormChanged = (): boolean => {
    if (!initialValues) return false;

    const currentValues = formik.values;

    // Сравниваем все поля формы
    return (
      currentValues.first_name !== initialValues.first_name ||
      currentValues.last_name !== initialValues.last_name ||
      currentValues.patronymic_name !== initialValues.patronymic_name ||
      currentValues.country != initialValues.country ||
      currentValues.town != initialValues.town ||
      currentValues.expected_position != initialValues.expected_position ||
      currentValues.expected_salary !== initialValues.expected_salary ||
      currentValues.expected_salary_currency !== initialValues.expected_salary_currency ||
      currentValues.internal_rate != initialValues.internal_rate ||
      currentValues.contacts !== initialValues.contacts ||
      currentValues.contact_email !== initialValues.contact_email ||
      currentValues.contact_phone !== initialValues.contact_phone ||
      currentValues.contact_telegram !== initialValues.contact_telegram ||
      currentValues.contact_whatsapp !== initialValues.contact_whatsapp ||
      currentValues.birthday !== initialValues.birthday ||
      currentValues.comments !== initialValues.comments ||
      currentValues.source != initialValues.source ||
      currentValues.grade != initialValues.grade ||
      currentValues.url !== initialValues.url ||
      JSON.stringify(currentValues.skills_ids) !== JSON.stringify(initialValues.skills_ids) ||
      JSON.stringify(currentValues.attachments) !== JSON.stringify(initialValues.attachments) ||
      currentValues.department_id != initialValues.department_id ||
      currentValues.channel_id != initialValues.channel_id
    );
  };

  const formik = useFormik<IUpdateCandidateBody>({
    initialValues: {
      first_name: "",
      last_name: "",
      patronymic_name: "",
      country: null,
      town: undefined,
      expected_position: "",
      expected_salary: "",
      internal_rate: "",
      contacts: "",
      contact_email: "",
      contact_phone: "",
      contact_telegram: "",
      contact_whatsapp: "",
      birthday: "",
      attachments: [],
      comments: "",
      source: null,
      grade: null,
      skills_ids: [],
      url: "",
      expected_salary_currency: null,
      department_id: null,
      channel_id: null,
    },
    onSubmit: () => {},
  });

  // Мемоизированная функция для обновления значений формы
  const updateFormValues = useCallback((candidate: typeof currentCandidate) => {
    if (!candidate) return;

    // Создаем объект с начальными значениями
    const newValues: IUpdateCandidateBody = {
      first_name: candidate.first_name ?? "",
      last_name: candidate.last_name ?? "",
      patronymic_name: candidate.patronymic_name ?? "",
      country: candidate.country?.id ?? null,
      town: candidate.town ?? undefined,
      expected_position: candidate.expected_position ?? "",
      expected_salary: String(candidate?.expected_salary?.sum ?? ""),
      expected_salary_currency: candidate?.expected_salary?.currency?.id ?? null,
      internal_rate: candidate.internal_rate ?? "",
      contacts: candidate.contacts ?? "",
      contact_email: candidate.contact_email ?? "",
      contact_phone: candidate.contact_phone ?? "",
      contact_telegram: candidate.contact_telegram ?? "",
      contact_whatsapp: candidate.contact_whatsapp ?? "",
      birthday: candidate.birthday ?? "",
      attachments: [],
      comments: candidate.comments ?? "",
      source: candidate.source?.id ?? null,
      grade: candidate.grade?.id ?? null,
      skills_ids: candidate.skills?.map((el) => String(el.id)) ?? [],
      url: candidate.url ?? "",
      department_id: candidate.department?.id ?? null, // TODO: добавить когда будет поле в ISingleCandidate
      channel_id: candidate.channel?.id ?? null, // TODO: добавить когда будет поле в ISingleCandidate
    };

    // Устанавливаем значения в форму
    Object.entries(newValues).forEach(([key, value]) => {
      formik.setFieldValue(key, value);
    });

    // Сохраняем изначальные значения для сравнения
    setInitialValues(newValues);
  }, []);

  useEffect(() => {
    updateFormValues(currentCandidate);
  }, [currentCandidate, updateFormValues]);

  useEffect(() => {
    dispatch(fetchSingleCandidate(Number(id)));
  }, [dispatch, id]);

  return (
    <Layout>
      <PageLoaderWrapper isLoading={isLoading} loadingText="Загрузка данных кандидата для редактирования...">
        <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
          <Text color="gray.500" cursor="pointer" onClick={() => navigate(-1)} display="flex" alignItems="center" gap="8px">
            <ArrowBackIcon /> Назад
          </Text>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex flexDirection="column" gap="4px">
              <Text fontSize={30} lineHeight="36px" fontWeight={700} color="#3b515e">
                Редактировать кандидата
              </Text>
              <Text fontSize={16} lineHeight="24px" fontWeight={400} color="#65758b">
                Редактирование кандидата в базе
              </Text>
            </Flex>
          </Flex>
          <UpdateCandidateBlock formik={formik} />
          <Flex gap="16px" justifyContent="flex-end">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Отмена
            </Button>
            <Button
              onClick={() => {
                dispatch(
                  fetchUpdateCandidate({
                    id: Number(id),
                    params: formik.values,
                  }),
                ).then((res: any) => {
                  if (res.payload.id) {
                    navigate(`/candidate/${id}`);
                  }
                });
              }}
              bg="#61a6fa"
              color="white"
              _hover={{ bg: "#61a6fa", opacity: 0.8 }}
              isDisabled={
                formik.values.last_name === "" ||
                formik.values.first_name === "" ||
                formik.values.expected_position === "" ||
                formik.values.source === null ||
                !hasFormChanged() ||
                (isAdmin && formik.values.channel_id === null)
              }
            >
              Сохранить
            </Button>
          </Flex>
        </Flex>
      </PageLoaderWrapper>
    </Layout>
  );
};
